import React, { useState } from 'react';
import { HBox, VBox } from '../../layout';
import { Icon, LoadMask } from '../../basic';
import { intl, useZoomBreakpoint } from '../../../lib';
import { convertNumberToFileSize, getIconByMimeType } from '../../../util';
import type { CommonMessageProps } from './ChatMessageFrame';
import { ChatMessageFrame } from './ChatMessageFrame';
import { AccessibleClickableContainer } from '../../accessibility';

/**
 * Props for a {@link ChatFileMessage} component.
 */
export type ChatFileMessageProps = CommonMessageProps & {
  fileName: string;
  mimeType?: string;
  size?: number;
  onDownload?: () => Promise<void> | void;
};

export default function ChatFileMessage({
  person,
  fileName,
  mimeType,
  size,
  onDownload,
  status,
  ...frameProps
}: Readonly<ChatFileMessageProps>): React.JSX.Element {
  const [downloading, setDownloading] = useState(false);
  // Not on the server yet: no download or reactions.
  const isPending = status === 'sending';

  const handleDownload = async (): Promise<void> => {
    if (downloading) {
      return;
    }
    setDownloading(true);
    try {
      await onDownload?.();
    } finally {
      setDownloading(false);
    }
  };
  const isZoomed = useZoomBreakpoint(1.3);

  const content = (
    <>
      <div className="sr-only">{intl('common.chatmessage.download')}</div>
      <Icon
        aria-label={undefined}
        iconName={getIconByMimeType(mimeType, fileName)}
        className="file-message-icon"
      />
      <VBox className="file-message-detail">
        <div className="file-message-name">{fileName}</div>
        {size !== undefined && (
          <div className="file-message-size gray">
            {convertNumberToFileSize(size)}
          </div>
        )}
      </VBox>
    </>
  );
  const downloadIcon = (
    <div className="file-message-download-icon">
      {isPending || downloading ? (
        <LoadMask masked={true} data-testid="chat-file-download-progress" />
      ) : (
        <Icon iconName="download" aria-label={undefined} />
      )}
    </div>
  );

  return (
    <ChatMessageFrame
      prefix="file"
      person={person}
      status={status}
      reactionsDisabled={isPending}
      {...frameProps}
    >
      <HBox
        className={`file-message-body ${person.isSender ? 'sender' : ''} ${
          isPending ? 'pending' : ''
        } ${isZoomed ? 'zoomed' : ''}`}
        onClick={e => e.stopPropagation()}
      >
        {onDownload ? (
          <AccessibleClickableContainer
            disabled={isPending}
            aria-disabled={downloading}
            className="file-message-download"
            onClick={handleDownload}
            data-testid="chat-file-download"
          >
            {content}
            {downloadIcon}
          </AccessibleClickableContainer>
        ) : (
          <div className="file-message-download">
            {content}
            <div className="file-message-download-icon" />
          </div>
        )}
      </HBox>
    </ChatMessageFrame>
  );
}
